/**
 * usePdfParser Hook
 * Parses PDF files locally using PDF.js - no server upload needed
 */
import { useState, useCallback, useRef } from 'react';
import { Logger } from '../../../../utils/logger';

const log = Logger.create('usePdfParser');

// PDF.js will be loaded dynamically
let pdfjsLib = null;

/**
 * Load PDF.js dynamically
 */
async function loadPdfJs() {
  if (pdfjsLib) return pdfjsLib;
  
  try {
    // Dynamic import of pdfjs-dist
    pdfjsLib = await import('pdfjs-dist');
    
    // Set up worker from CDN
    pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/4.10.38/pdf.worker.min.mjs`;
    
    return pdfjsLib;
  } catch (error) {
    log.error('Failed to load PDF.js', error);
    throw new Error('PDF.js konnte nicht geladen werden. Bitte führe "npm install" aus.');
  }
}

/**
 * Parse a PDF file and extract text from all pages
 * @param {File} file - The PDF file to parse
 * @returns {Promise<Object>} Object with numPages and pages array
 */
async function parsePdfFileInternal(file) {
  const pdfjs = await loadPdfJs();
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  
  const pages = [];
  
  for (let pageNum = 1; pageNum <= pdf.numPages; pageNum++) {
    const page = await pdf.getPage(pageNum);
    
    // Extract text content
    const textContent = await page.getTextContent();
    const text = textContent.items
      .map(item => item.str)
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();
    
    // Get page dimensions for thumbnail
    const viewport = page.getViewport({ scale: 1.0 });
    
    pages.push({
      pageNumber: pageNum,
      text,
      width: viewport.width,
      height: viewport.height,
      selected: true, // All pages selected by default
      edited: false,
      originalText: text
    });
  }
  
  return { numPages: pdf.numPages, pages };
}

/**
 * Parse a text file
 * @param {File} file - The text file to parse
 * @returns {Promise<Array>} Array with single page object
 */
async function parseTextFile(file) {
  const text = await file.text();
  return [{
    pageNumber: 1,
    text,
    selected: true,
    edited: false,
    originalText: text
  }];
}

/**
 * Render a PDF page to canvas for thumbnail
 * @param {File} file - The PDF file
 * @param {number} pageNum - Page number to render
 * @param {number} scale - Scale factor for rendering
 * @returns {Promise<string>} Base64 data URL of the rendered page
 */
export async function renderPageThumbnail(file, pageNum, scale = 0.3) {
  const pdfjs = await loadPdfJs();
  const arrayBuffer = await file.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: arrayBuffer }).promise;
  const page = await pdf.getPage(pageNum);
  
  const viewport = page.getViewport({ scale });
  
  // Create canvas
  const canvas = document.createElement('canvas');
  canvas.width = viewport.width;
  canvas.height = viewport.height;
  
  const ctx = canvas.getContext('2d');
  
  await page.render({
    canvasContext: ctx,
    viewport
  }).promise;
  
  return canvas.toDataURL('image/jpeg', 0.7);
}

/**
 * Main hook for PDF parsing
 */
export function usePdfParser() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({ current: 0, total: 0 });
  
  /**
   * Parse a single PDF file
   * @param {File} file - PDF file to parse
   * @returns {Promise<Object>} Parsed file object with pages
   */
  const parsePdfFile = useCallback(async (file) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await parsePdfFileInternal(file);
      setIsLoading(false);
      return result;
    } catch (err) {
      log.error('Error parsing PDF', err);
      setError(err.message || 'Fehler beim Parsen der PDF');
      setIsLoading(false);
      return null;
    }
  }, []);
  
  /**
   * Parse a text file
   * @param {File} file - Text file to parse
   * @returns {Promise<string>} Text content
   */
  const parseTextFile = useCallback(async (file) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const text = await file.text();
      setIsLoading(false);
      return text;
    } catch (err) {
      log.error('Error parsing text file', err);
      setError(err.message || 'Fehler beim Lesen der Datei');
      setIsLoading(false);
      return null;
    }
  }, []);
  
  /**
   * Estimate token count for text
   * Rough estimation: ~4 characters per token
   */
  const estimateTokens = useCallback((text) => {
    if (!text) return 0;
    return Math.ceil(text.length / 4);
  }, []);
  
  return {
    parsePdfFile,
    parseTextFile,
    isLoading,
    error,
    progress,
    estimateTokens
  };
}

export default usePdfParser;
